import {
    Box,
    Button,
    capitalize,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import {PhoneNumber, PhoneNumberListe} from "../../api";
import {useListPhoneNumbers} from "../../client/queries";
import {usePhonenumbersHandling} from "../../hooks/phonenumbers/usePhonenumbersHandling";
import DialogCreatePhonenumber from "../../components/phonenumbers/DialogCreatePhonenumber";
import MultipleGuard from "../../components/guards/MultipleGuard";
import ErrorAlert from "../../components/ErrorAlert";
import Layout from "../../components/Layout";
import {permissions} from "../../permissions";
import {notPermitted} from "../../errors";

export default function PagePhonenumbers() {
    const {t: translation} = useTranslation('phonenumbers');
    const navigate = useNavigate();
    const list: PhoneNumberListe = useListPhoneNumbers();
    const {deletePhoneNumberMutation, canEditPhoneNumber} = usePhonenumbersHandling();
    const canEdit = canEditPhoneNumber();
    const [createOpen, setCreateOpen] = useState(false);
    const [toDelete, setToDelete] = useState<PhoneNumber | null>(null);

    const confirmDelete = () => {
        if (toDelete) deletePhoneNumberMutation.mutate(toDelete.id);
        setToDelete(null);
    };

    return (
        <Layout>
        <MultipleGuard
            permissions={[permissions.phonenumbers.show, permissions.phonenumbers.edit]}
            fallback={<ErrorAlert error={notPermitted()}/>}
        >
            <Paper sx={{p: 2}}>
            <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{capitalize(translation('phonenumbers-title'))}</Typography>
                    <Button variant="contained" color="secondary" disabled={!canEdit} onClick={() => setCreateOpen(true)}>
                        {capitalize(translation('phonenumber-create'))}
                    </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{capitalize(translation('phonenumber-number'))}</TableCell>
                                <TableCell>{capitalize(translation('phonenumber-descriptor'))}</TableCell>
                                <TableCell>{capitalize(translation('phonenumber-fallback'))}</TableCell>
                                <TableCell>{capitalize(translation('phonenumber-inbound-config'))}</TableCell>
                                <TableCell>{capitalize(translation('phonenumber-usages'))}</TableCell>
                                <TableCell align="right">{capitalize(translation('phonenumber-actions'))}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.items.map((p: PhoneNumber) => (
                                <TableRow
                                    key={p.id}
                                    hover
                                    onClick={() => navigate(`/phonenumbers/${p.id}`)}
                                    sx={{cursor: 'pointer'}}
                                >
                                    <TableCell>{p.number}</TableCell>
                                    <TableCell>
                                        {p.descriptor
                                            ? p.descriptor
                                            : <Typography color="text.secondary" variant="body2">—</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        {p.isGlobal
                                            ? <Chip color="success" size="small" label={capitalize(translation('boolean-true'))}/>
                                            : <Chip variant="outlined" size="small" label={capitalize(translation('boolean-false'))}/>}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {p.activeIcpFallbackPromptId
                                                ? <Chip color="success" size="small" label={capitalize(translation('phonenumber-inbound-config-enabled'))}/>
                                                : <Chip variant="outlined" size="small" label={capitalize(translation('phonenumber-inbound-config-disabled'))}/>}
                                            {p.usages.filter(u => u.type === 'AUFGABE' && u.use_inbound && !u.inherited).length === 1 &&
                                                <Chip size="small" label={capitalize(translation('phonenumber-inbound-config-overridden'))}
                                                    sx={{bgcolor: '#7c3aed', color: '#fff'}}/>}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {p.usages.length === 0
                                            ? <Typography color="text.secondary" variant="body2">—</Typography>
                                            : <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {(() => {
                                                    const isOverridden = p.usages.filter(u => u.type === 'AUFGABE' && u.use_inbound && !u.inherited).length === 1;
                                                    return p.usages.map((u) => {
                                                        const tooltipParts = [
                                                            capitalize(translation(u.type === 'AUFGABE' ? 'phonenumber-usage-aufgabe' : 'phonenumber-usage-mandant')),
                                                            translation(u.inherited ? 'phonenumber-usage-inherited' : 'phonenumber-usage-direct'),
                                                        ];
                                                        const isOverridingAufgabe = isOverridden && u.type === 'AUFGABE' && u.use_inbound && !u.inherited;
                                                        if (isOverridingAufgabe) {
                                                            tooltipParts.push(translation('phonenumber-usage-inbound-active'));
                                                        }
                                                        return (
                                                            <Tooltip key={`${u.type}-${u.id}`} title={tooltipParts.join(' · ')}>
                                                                <Chip
                                                                    size="small"
                                                                    variant={u.inherited ? 'outlined' : 'filled'}
                                                                    color={isOverridingAufgabe ? undefined : u.type === 'AUFGABE' ? 'primary' : 'info'}
                                                                    icon={u.type === 'AUFGABE' ? <AssignmentOutlinedIcon fontSize="small"/> : <BusinessOutlinedIcon fontSize="small"/>}
                                                                    label={u.name}
                                                                    sx={isOverridingAufgabe ? {bgcolor: '#7c3aed', color: '#fff'} : undefined}
                                                                />
                                                            </Tooltip>
                                                        );
                                                    });
                                                })()}
                                            </Stack>}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title={capitalize(translation('phonenumber-edit'))}>
                                            <span>
                                                <IconButton size="small" onClick={() => navigate(`/phonenumbers/${p.id}`)}>
                                                    <EditOutlinedIcon fontSize="small"/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={capitalize(translation('phonenumber-delete'))}>
                                            <span>
                                                <IconButton size="small" color="error" disabled={!canEdit || deletePhoneNumberMutation.isLoading} onClick={() => setToDelete(p)}>
                                                    <DeleteOutlineIcon fontSize="small"/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {list.items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography color="text.secondary" variant="body2">{translation('phonenumbers-empty')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
            </Paper>
            <DialogCreatePhonenumber open={createOpen} onClose={() => setCreateOpen(false)}/>
            <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
                <DialogTitle>{capitalize(translation('phonenumber-delete'))}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {translation('phonenumber-delete-confirm', {number: toDelete?.number ?? ''})}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)}>{capitalize(translation('abort'))}</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete}>
                        {capitalize(translation('phonenumber-delete'))}
                    </Button>
                </DialogActions>
            </Dialog>
        </MultipleGuard>
        </Layout>
    );
}
